import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Copy,
  Facebook,
  Heart,
  Instagram,
  MessageCircle,
  Share2,
  ShoppingBag,
  Star,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, inr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FoodSymbol } from "@/components/FoodSymbol";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ProductCard = ({ product, bogoOffer = null }) => {
  const { add, items } = useCart();
  const { user, wishlistIds, toggleWishlist } = useAuth();
  const nav = useNavigate();
  const isHamper = product.product_type === "gift_box";
  const displayPrice = product.variants?.[0]?.price ?? product.price;
  const displayMrp = product.variants?.[0]?.mrp ?? product.mrp;
  const displayStock = Number(product.variants?.[0]?.stock ?? product.stock);
  const hasLowStock = Number.isFinite(displayStock) && displayStock > 0 && displayStock <= 10;
  const off = displayMrp > displayPrice
    ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
    : 0;
  // Badge shows live cart quantity for this product (sum across variants). Resets to 0 when removed from cart.
  const cartQty = items
    .filter((i) => i.product_id === product.id)
    .reduce((s, i) => s + i.quantity, 0);
  const [pulse, setPulse] = useState(false);
  const wishlisted = wishlistIds.includes(product.id);
  const productUrl = `${window.location.origin}/product/${product.slug || product.id}`;
  const shareText = `${product.name} — ${productUrl}`;
  const handleWishlist = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    try {
      const action = await toggleWishlist(product.id);
      toast.success(action === "added" ? "Added to wishlist" : "Removed from wishlist");
    } catch {
      toast.error("Could not update wishlist");
    }
  };
  const openShareUrl = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const copyProductLink = async (message) => {
    try {
      await navigator.clipboard.writeText(productUrl);
      toast.success(message);
    } catch {
      toast.error("Could not copy the product link");
    }
  };
  const shareOnWhatsApp = () => {
    openShareUrl(`https://wa.me/?text=${encodeURIComponent(shareText)}`);
  };
  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`;
    openShareUrl(url);
  };
  const shareOnX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    openShareUrl(url);
  };
  const actionVisibilityClass = [
    "opacity-100 transition-[opacity,transform,background-color] duration-300",
    "md:pointer-events-none md:translate-y-1 md:opacity-0",
    "md:group-hover:pointer-events-auto md:group-hover:translate-y-0",
    "md:group-hover:opacity-100 md:group-focus-within:pointer-events-auto",
    "md:group-focus-within:translate-y-0",
    "md:group-focus-within:opacity-100",
  ].join(" ");

  return (
    <div
      className={[
        "group relative flex flex-col overflow-hidden rounded-lg border border-[#E7E2D9]",
        "bg-white shadow-[0_1px_2px_rgba(30,42,36,0.04)] transition-[box-shadow,transform]",
        "duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(30,42,36,0.10)]",
        displayStock <= 0 ? "opacity-70 grayscale-[0.35]" : "",
      ].join(" ")}
      data-testid={`product-card-${product.id}`}
    >
      <Link
        to={`/product/${product.slug || product.id}`}
        className="relative block aspect-[4/5] overflow-hidden bg-[#F7F5F0]"
      >
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className={[
            "h-full w-full object-cover transition-transform duration-500",
            "group-hover:scale-[1.035]",
          ].join(" ")}
        />
        {off > 0 && (
          <span
            className={[
              "absolute left-2.5 top-2.5 bg-white/90 px-2 py-1 text-[10px] font-medium",
              "text-[#A3542E] shadow-sm",
            ].join(" ")}
          >
            −{off}%
          </span>
        )}
        {(product.is_new || product.is_best_seller || bogoOffer) && (
          <div className="absolute bottom-2.5 left-2.5 flex flex-col items-start gap-1">
            {(product.is_new || product.is_best_seller) && (
              <span className="bg-[#1E2A24]/90 px-2 py-1 text-[9px] font-medium uppercase tracking-[0.14em] text-white">
                {product.is_new ? "New" : "Best seller"}
              </span>
            )}
            {bogoOffer && (
              <span
                className="bg-[#2D5A47] px-2 py-1 text-[9px] font-medium uppercase tracking-[0.12em] text-white"
                data-testid={`product-card-bogo-badge-${product.id}`}
              >
                Buy 1 Get 1 Free
              </span>
            )}
          </div>
        )}
      </Link>
      <div
        className={[
          "absolute right-2.5 top-2.5 z-10 flex items-center gap-1.5",
          actionVisibilityClass,
        ].join(" ")}
        data-testid={`product-card-actions-${product.id}`}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E7E2D9] bg-white/95 text-[#64748B] shadow-sm transition-colors hover:text-[#2D5A47]"
              aria-label={`Share ${product.name}`}
              data-testid={`product-card-share-${product.id}`}
            >
              <Share2 size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="z-[60] w-52 border-[#E7E2D9] bg-white p-1 shadow-lg"
            data-testid={`product-card-share-menu-${product.id}`}
          >
            <DropdownMenuLabel className="px-3 py-2 text-xs font-semibold text-[#64748B]">
              Share product
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#E2DCD2]" />
            <DropdownMenuItem
              onSelect={shareOnWhatsApp}
              className="gap-2 text-[#1E2A24] focus:bg-[#EBF2EE]"
              data-testid={`product-card-share-whatsapp-${product.id}`}
            >
              <MessageCircle size={16} className="text-[#2D5A47]" /> WhatsApp
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => copyProductLink("Product link copied")}
              className="gap-2 text-[#1E2A24] focus:bg-[#EBF2EE]"
              data-testid={`product-card-share-copy-link-${product.id}`}
            >
              <Copy size={16} className="text-[#2D5A47]" /> Copy Link
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => copyProductLink(
                "Link copied — paste in your Instagram bio or story",
              )}
              className="gap-2 text-[#1E2A24] focus:bg-[#EBF2EE]"
              data-testid={`product-card-share-instagram-${product.id}`}
            >
              <Instagram size={16} className="text-[#C86D42]" /> Instagram
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={shareOnFacebook}
              className="gap-2 text-[#1E2A24] focus:bg-[#EBF2EE]"
              data-testid={`product-card-share-facebook-${product.id}`}
            >
              <Facebook size={16} className="text-[#4267B2]" /> Facebook
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={shareOnX}
              className="gap-2 text-[#1E2A24] focus:bg-[#EBF2EE]"
              data-testid={`product-card-share-x-${product.id}`}
            >
              <span className="flex h-4 w-4 items-center justify-center text-xs font-bold">X</span>
              X
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={handleWishlist}
          className={[
            "flex h-7 w-7 items-center justify-center rounded-full border border-[#E7E2D9] shadow-sm",
            "transition-colors",
            wishlisted ? "bg-red-50 text-red-500" : "bg-white text-[#64748B]",
          ].join(" ")}
          data-testid={`wishlist-toggle-${product.id}`}
          aria-label={
            wishlisted
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
        >
          <Heart size={14} fill={wishlisted ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="flex flex-1 flex-col justify-between p-5">
        <div>
          <Link
            to={`/brand/${(product.brand || "eira").toLowerCase().replace(/\s+/g, "-")}`}
            className="text-[10px] uppercase tracking-[0.18em] text-[#8C6D46] hover:text-[#C86D42]"
          >
            {product.brand || "Eira"}
          </Link>
          <Link to={`/product/${product.slug || product.id}`}>
            <div className="mt-1 flex items-start gap-1">
              <FoodSymbol
                value={product.food_symbol}
                className="mt-0.5 shrink-0"
                testId={`product-card-food-symbol-${product.id}`}
              />
              <h3
                className={[
                  "line-clamp-2 text-[15px] font-medium leading-6 text-[#1E2A24]",
                  "hover:text-[#C86D42]",
                ].join(" ")}
              >
                {product.name}
              </h3>
            </div>
          </Link>
          {(product.review_count > 0 || product.rating > 0) && (
            <div className="flex items-center gap-1 mt-2 text-xs text-[#8C6D46]">
              <Star size={12} fill="#D99B26" strokeWidth={0} /> {product.rating?.toFixed(1) || "0.0"}
              <span className="text-gray-400">({product.review_count || 0})</span>
            </div>
          )}
          {bogoOffer && (
            <div
              className="mt-3 border-l-2 border-[#2D5A47] bg-[#EBF2EE] px-2 py-1.5 text-xs text-[#1E2A24]"
              data-testid={`product-card-bogo-offer-${product.id}`}
            >
              <span className="font-semibold">Buy 1 Get 1 Free</span>
              <span className="ml-1 text-[#64748B]">Use code {bogoOffer.code}</span>
            </div>
          )}
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            {hasLowStock ? (
              <p
                className="mb-1 text-xs font-semibold text-amber-700"
                data-testid={`low-stock-indicator-${product.id}`}
              >
                Only {displayStock} left
              </p>
            ) : null}
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold text-[#1E2A24]">
                {isHamper && <span className="mr-1 text-xs font-normal">From</span>}
                {inr(displayPrice)}
              </span>
              {off > 0 && (
                <span className="text-xs text-gray-400 line-through">{inr(displayMrp)}</span>
              )}
            </div>
          </div>
          {isHamper ? <Link to={`/product/${product.slug || product.id}`} className="w-full border border-[#2D5A47] px-3 py-2 text-center text-xs font-semibold text-[#2D5A47] hover:bg-[#EBF2EE] sm:w-auto" data-testid={`build-gift-box-${product.id}`}>Build box</Link> : displayStock <= 0 ? (
            <button type="button" onClick={() => nav(`/product/${product.slug || product.id}`)} className="w-full border border-[#2D5A47] px-3 py-2 text-xs font-semibold text-[#2D5A47] sm:w-auto" data-testid={`notify-me-${product.id}`}>Notify me</button>
          ) : <button
            data-testid={`add-cart-${product.id}`}
            onClick={() => {
              const variants = product.variants || [];
              if (variants.length > 1) {
                toast.error("Select a size or variant on the product page first");
                nav(`/product/${product.slug || product.id}`);
                return;
              }
              if (!add(product, variants[0] || null)) return;
              toast.success("Item added to cart", { duration: 2500 });
              setPulse(true);
              setTimeout(() => setPulse(false), 400);
              api.post(`/products/${product.id}/track-click`, {}).catch(() => {});
            }}
            className={[
              "relative inline-flex w-full min-w-[76px] items-center justify-center gap-1.5 whitespace-nowrap",
              "border border-[#2D5A47] px-3 py-2 text-xs font-semibold",
              "transition-[color,background-color,transform]",
              cartQty > 0
                ? "bg-[#2D5A47] text-white hover:bg-[#214435]"
                : "text-[#2D5A47] hover:bg-[#2D5A47] hover:text-white",
              "sm:w-auto",
              "active:scale-95",
              pulse ? "scale-105" : "",
            ].join(" ")}
          >
            <ShoppingBag size={14} className="shrink-0" />
            <span>Add</span>
            {cartQty > 0 && (
              <span
                data-testid={`click-count-${product.id}`}
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C86D42] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
              >
                {cartQty > 99 ? "99+" : cartQty}
              </span>
            )}
          </button>}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
